import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { Dispatch } from '@reduxjs/toolkit';
import { isEqual, omit, pick } from 'lodash';
import type { RootState } from '../../redux/store';
import { EWS_DURATION_KEYS, EWS_TERM_TAB_KEY_MAP, FEATURE_NAMES } from '../../common/constants';
import type { EwsDuration, EwsDurationKey, TermTabType } from '../../common/constants';
import type { InvestmentFundOptions } from '../global/globalSlice';
import { checkFFEnabled, getClientJourneyFlowType } from '../../utils';

// Flow2 plans saved at least once keep Profile & Finances and Goals edits in the store until the plan is saved
// again, so nothing re-stamps the edited accounts and goals. This slice records, per term, when they last changed
// without being saved; the Update Funding & Goals pop-up shows it as "Last Updated" (step 1 accounts, step 2 goals).

export type UnsavedChangeScope = 'accounts' | 'goals';

type TermChangeDates = Partial<Record<EwsDurationKey, string>>;
type UnsavedTermChanges = Partial<Record<UnsavedChangeScope, EwsDurationKey[]>>;

export interface UnsavedPlanChangesState {
    // Plan the dates belong to; dates recorded for another plan are ignored.
    proposalId: string;
    accounts: TermChangeDates;
    goals: TermChangeDates;
}

type AccountRecord = Record<string, unknown> & {
    accountId?: string;
    accountType?: string;
    contributions?: Array<Record<string, unknown>>;
};

type GoalRecord = { goalId?: string; data?: Record<string, unknown> & { goalId?: string; goalCategoryOrder?: number } };

type ClientAccounts = Record<string, unknown> & { investmentsSources?: AccountRecord[] };

type PlanEditFlags = { isPlanRun?: boolean; isPlanSavedOnce?: boolean };

// Fields the editors add, strip or reset on every save; they don't make an account or goal changed.
const BOOKKEEPING_FIELDS = ['createdAt', 'updatedAt', 'update', 'isNew', 'localId'];
const DATE_FIELDS = ['createdAt', 'updatedAt'];

const initialState: UnsavedPlanChangesState = { proposalId: '', accounts: {}, goals: {} };

const unsavedPlanChangesSlice = createSlice({
    name: 'unsavedPlanChanges',
    initialState,
    reducers: {
        recordTermChanges: (
            state,
            { payload }: PayloadAction<UnsavedTermChanges & { proposalId: string; date: string }>
        ) => {
            if (state.proposalId !== payload.proposalId) {
                state.proposalId = payload.proposalId;
                state.accounts = {};
                state.goals = {};
            }
            payload.accounts?.forEach((term) => {
                state.accounts[term] = payload.date;
            });
            payload.goals?.forEach((term) => {
                state.goals[term] = payload.date;
            });
        },
        clearUnsavedPlanChanges: () => initialState
    }
});

export const { clearUnsavedPlanChanges } = unsavedPlanChangesSlice.actions;

export default unsavedPlanChangesSlice.reducer;

const isFlow2Plan = (state: RootState): boolean => getClientJourneyFlowType(state?.auth?.user?.tenant) === 'flow2';

/**
 * Whether Profile & Finances and Goals edits stay in the store until the plan is saved again instead of going to the
 * backend: in flow2 once the plan has been saved (behind the complete-to-draft flag), in other flows once it has run.
 * A save in progress can pass its own flags as `pendingFlags`.
 */
export const shouldKeepPlanEditsInStore = (state: RootState, pendingFlags?: PlanEditFlags): boolean => {
    const savedFlags = state?.adviceGoals?.global as PlanEditFlags | undefined;
    const isPlanRun = Boolean(pendingFlags?.isPlanRun || savedFlags?.isPlanRun);
    const isPlanSavedOnce = Boolean(pendingFlags?.isPlanSavedOnce || savedFlags?.isPlanSavedOnce);
    const isCompleteToDraftEnabled = Boolean(checkFFEnabled(FEATURE_NAMES['AEX-3590_3604_NEW_FLOW_UPDATE_SAVE_PLAN']));
    return isPlanRun && (!isFlow2Plan(state) || (isPlanSavedOnce && isCompleteToDraftEnabled));
};

/** Date of the latest unsaved change to a term's accounts or goals, for the plan in the store. */
export const selectUnsavedChangeDate = (
    state: RootState,
    scope: UnsavedChangeScope,
    term: EwsDurationKey
): string | undefined => {
    const changes = state.unsavedPlanChanges;
    return changes?.proposalId && changes.proposalId === state.adviceGoals?._id ? changes[scope][term] : undefined;
};

const isTerm = (term: EwsDurationKey | undefined): term is EwsDurationKey => term !== undefined;

const getAccountTerm = (source: AccountRecord, accountTypes: InvestmentFundOptions[]): EwsDurationKey | undefined => {
    const duration = accountTypes.find((type) => type.value === source.accountType)?.duration;
    return duration ? EWS_DURATION_KEYS[duration as EwsDuration] : undefined;
};

const getGoalTerm = (field: GoalRecord): EwsDurationKey | undefined => {
    const order = field.data?.goalCategoryOrder;
    return typeof order === 'number' ? EWS_TERM_TAB_KEY_MAP[(order - 1) as TermTabType] : undefined;
};

/** Items added, edited (both versions, as an edit can move an item to another term) or deleted between two lists. */
const getChangedItems = <T>(
    previous: T[],
    next: T[],
    // eslint-disable-next-line no-unused-vars
    getId: (item: T) => unknown,
    // eslint-disable-next-line no-unused-vars
    isChanged: (before: T, after: T) => boolean
): T[] => {
    const previousById = new Map(previous.map((item) => [getId(item), item]));
    const nextIds = new Set(next.map(getId));
    const addedOrEdited = next.flatMap((item) => {
        const before = previousById.get(getId(item));
        if (!before) return [item];
        return isChanged(before, item) ? [before, item] : [];
    });
    const deleted = previous.filter((item) => !nextIds.has(getId(item)));
    return [...addedOrEdited, ...deleted];
};

const toComparableAccount = (source: AccountRecord): Record<string, unknown> => ({
    ...omit(source, BOOKKEEPING_FIELDS),
    amount: Number(source.amount) || 0,
    contributions: (source.contributions ?? []).map((contribution) => omit(contribution, BOOKKEEPING_FIELDS))
});

// The inline Accounts table resends each account as a reduced projection, so only the fields it sends are compared.
const isAccountChanged = (before: AccountRecord, after: AccountRecord): boolean => {
    const previous = toComparableAccount(before);
    const next = toComparableAccount(after);
    return Object.keys(next).some((field) => !isEqual(previous[field], next[field]));
};

const isGoalChanged = (before: GoalRecord, after: GoalRecord): boolean =>
    !isEqual(omit(before.data, BOOKKEEPING_FIELDS), omit(after.data, BOOKKEEPING_FIELDS));

/** Terms whose accounts were added, edited or deleted between two `investmentsSources` lists. */
export const getChangedAccountTerms = (
    previous: AccountRecord[],
    next: AccountRecord[],
    accountTypes: InvestmentFundOptions[]
): EwsDurationKey[] => {
    const changed = getChangedItems(previous, next, (source) => source.accountId, isAccountChanged);
    return Array.from(new Set(changed.map((source) => getAccountTerm(source, accountTypes)).filter(isTerm)));
};

/** Terms whose goals were added, edited or deleted between two `formFields` lists. */
export const getChangedGoalTerms = (previous: GoalRecord[], next: GoalRecord[]): EwsDurationKey[] => {
    const changed = getChangedItems(previous, next, (field) => field.goalId ?? field.data?.goalId, isGoalChanged);
    return Array.from(new Set(changed.map(getGoalTerm).filter(isTerm)));
};

/** Restores each account's existing dates, which the editors drop when they resend the accounts. */
export const keepAccountDates = (previous: AccountRecord[], next: AccountRecord[]): AccountRecord[] => {
    const previousById = new Map(previous.map((source) => [source.accountId, source]));
    return next.map((source) => {
        const before = source.accountId ? previousById.get(source.accountId) : undefined;
        return before ? { ...pick(before, DATE_FIELDS), ...source } : source;
    });
};

const dispatchTermChanges = (dispatch: Dispatch, state: RootState, changes: UnsavedTermChanges): void => {
    if (!changes.accounts?.length && !changes.goals?.length) return;
    dispatch(
        unsavedPlanChangesSlice.actions.recordTermChanges({
            ...changes,
            proposalId: state.adviceGoals?._id ?? '',
            date: new Date().toISOString()
        })
    );
};

/** Records today as the unsaved change date of the given terms, when the flow2 plan keeps its edits in the store. */
export const recordUnsavedTermChanges =
    (changes: UnsavedTermChanges) =>
    (dispatch: Dispatch, getState: () => RootState): void => {
        const state = getState();
        if (isFlow2Plan(state) && shouldKeepPlanEditsInStore(state)) {
            dispatchTermChanges(dispatch, state, changes);
        }
    };

/**
 * For a store-only account save: records the terms whose accounts it adds, edits or deletes, and returns the
 * accounts with each existing account's dates kept.
 */
export const trackUnsavedAccountChanges =
    (accounts: ClientAccounts | undefined) =>
    (dispatch: Dispatch, getState: () => RootState): ClientAccounts | undefined => {
        const nextSources = accounts?.investmentsSources;
        if (!accounts || !Array.isArray(nextSources)) return accounts;
        const state = getState();
        const previousSources = (state.adviceClient?.accounts?.investmentsSources ?? []) as AccountRecord[];
        const accountTypes = state.global?.orgInfo?.investmentAccountType?.en ?? [];
        dispatchTermChanges(dispatch, state, {
            accounts: getChangedAccountTerms(previousSources, nextSources, accountTypes)
        });
        return { ...accounts, investmentsSources: keepAccountDates(previousSources, nextSources) };
    };

/** For a store-only goals save: records the terms whose goals it adds, edits or deletes. */
export const trackUnsavedGoalChanges =
    (formFields: GoalRecord[] | undefined) =>
    (dispatch: Dispatch, getState: () => RootState): void => {
        if (!Array.isArray(formFields)) return;
        const state = getState();
        dispatchTermChanges(dispatch, state, {
            goals: getChangedGoalTerms(state.adviceGoals?.formFields ?? [], formFields)
        });
    };
